import { useQuery } from "react-query";
import { useAgentsHttp } from "../useAgentsHttp";
import { QK } from "../queryKeys";
import type { AITrace } from "../models/aiTrace";

// Read the persisted AI trace for one artefact. Backend route is
// `POST /ai-trace/get` (Lambdalith), which validates `surface` against a
// known allowlist and returns `{trace: AITrace | null}` (null when no trace
// was persisted / the bucket isn't configured in this env). A trace too large
// for a Lambda response comes back as `trace_url`, a presigned S3 GET.
interface AiTraceGetResponse {
  trace: AITrace | null;
  trace_url?: string;
}

const QUERY_KEY = QK.aiTraceGet;

export const useAiTrace = (surface?: string, artefactId?: string) => {
  const http = useAgentsHttp();
  const enabled = Boolean(surface && artefactId);
  return useQuery<AITrace | null>(
    [QUERY_KEY, surface ?? null, artefactId ?? null],
    async () => {
      const resp = await http<AiTraceGetResponse>("/ai-trace/get", {
        surface,
        artefact_id: artefactId,
      });
      if (resp.trace_url) {
        // No Authorization header: the presigned URL is the credential.
        const served = await fetch(resp.trace_url);
        if (!served.ok) {
          throw new Error(`Could not load AI trace (${served.status})`);
        }
        return (await served.json()) as AITrace;
      }
      return resp.trace;
    },
    {
      enabled,
      refetchOnWindowFocus: false,
      // Traces are immutable once persisted - cache for the session.
      staleTime: Infinity,
    },
  );
};
